import React from 'react';
import styled from 'react-emotion';
import theme from '@jetshop/ui/utils/theme';
import { useIntl } from '@jetshop/intl';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';

export const CategoryHeaderWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  padding-top: 18px;
  padding-left: 0;
  padding-right: 0;
  margin-bottom: 16px;
  padding-bottom: 2rem;
  text-align: center;
  position: relative;
  width: 100%;
  ${theme('above.md')} {
    padding-top: 45px;
    padding-bottom: 0;
    margin-bottom: 0;
  }
  ${theme('above.lg')} {
    padding-top: 45px;
    padding-bottom: 1rem;
    margin-bottom: 0;
  }

  ${theme('below.md')} {
    width: auto;
    padding: 1.5rem;
    margin: 0;
    [data-channel='Eyewearstore'] & {
      padding: 3rem 1.5rem 0 1.5rem;
    }
  }
  .ExpandInfoLink,
  .MinimizeInfoLink {
    display: none;
  }
  .total-results {
    font-size: 10px;
    text-align: left;
  }
`;

export const CategoryName = styled('h1')`
  font-weight: 600;
  line-height: normal;
  font-size: 32px;
  margin-bottom: 10px;
`;

export const CategoryContent = styled('div')`
  font-weight: 300;
  line-height: 1.3;
  width: 100%;
  font-size: 14px;
  margin-bottom: 1rem;
  &.open {
    margin-bottom: 0;
  }

  p {
    margin-bottom: 1em;
  }

  overflow: hidden;
  max-height: ${({ hasReadMore }) => (hasReadMore ? '3.4rem' : 'none')};

  &.open {
    max-height: none;
  }
  .ExpandInfoText {
    padding-bottom: 1rem;
  }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  a,
  li {
    font-size: 14px;
  }
  li:first-child {
    display: none;
  }
`;

const ToggleReadMoreButton = styled('button')`
  ${theme('text.linkCopy')};
  .adobe-garamond-pro-ready & {
    font-family: ${theme('fontFamilies.serifGarmondPro')};
  }
  align-self: center;
  width: 100%;
  outline: 0;
  box-shadow: 0px calc(0.4em * -1) 10px 0px rgba(248, 248, 248, 0.6),
    0px calc(0.4em * -1) 10px 0px rgba(248, 248, 248, 0.6);
  transition: box-shadow 0.1s ease;
  background: transparent;
  color: rgba(0, 0, 0, 0.3);
  position: absolute;
  bottom: 1rem;
  @media (max-width: 1024px) {
    bottom: 1.5rem;
  }

  &.open {
    box-shadow: none;
    transition: box-shadow 0.1s ease;
    color: #28282c;
    bottom: 0.5rem;
  }
`;

const CategoryHeader = ({ category }) => {
  const t = useIntl();

  const breadcrumbProps = category.primaryRoute
    ? {
        breadcrumbText: category.breadcrumbText,
        parents: category.primaryRoute.parents,
      }
    : null;

  const container = React.useRef();

  const [showMore, setShowMore] = React.useState(false);
  const [hasReadMore, setHasReadMore] = React.useState(false);

  React.useEffect(() => {
    const hasElement = !!container.current?.querySelector('.ExpandInfoLink');
    setHasReadMore(hasElement);
  }, [container]);

  const toggleReadMore = () => setShowMore((showMore) => !showMore);

  return (
    <CategoryHeaderWrapper>
      <StyledBreadcrumbs {...breadcrumbProps} />

      <CategoryName data-testid="category-title">{category.name}</CategoryName>
      {category.hasSubcategories ? null : (
        <span className="total-results">
          {category?.products?.totalResults} {t('PRODUKTER')}
        </span>
      )}
      {category.content && (
        <CategoryContent
          innerRef={container}
          className={hasReadMore && showMore && 'open'}
          data-testid="category-description"
          hasReadMore={hasReadMore}
          dangerouslySetInnerHTML={{
            __html: category.content,
          }}
        />
      )}
      {hasReadMore && (
        <ToggleReadMoreButton
          onClick={toggleReadMore}
          className={showMore && 'open'}
        >
          {showMore ? 'Läs mindre' : 'Läs mer'}
        </ToggleReadMoreButton>
      )}

      {/* <Below
        breakpoint="md"
        render={() => (
          <SortOrderDropdown
            sortOrders={[
              {
                __typename: 'ProductSortOrder',
                defaultDirection: 'DESCENDING',
                text: 'Senast inkommet',
                value: 'PUBLISH_DATE'
              },
              {
                __typename: 'ProductSortOrder',
                defaultDirection: 'DESCENDING',
                text: 'Namn',
                value: 'NAME'
              },
              {
                __typename: 'ProductSortOrder',
                defaultDirection: 'DESCENDING',
                text: 'Pris',
                value: 'PRICE'
              }
            ]}
          />
        )}
      /> */}
    </CategoryHeaderWrapper>
  );
};

export default CategoryHeader;
